import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function MilestonesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({
    years: 0,
    orders: 0,
    brands: 0,
    logos: 0
  });
  const sectionRef = useRef<HTMLElement>(null);

  const milestones = [
    {
      number: 10,
      suffix: '+',
      label: 'Years of Leadership',
      description: 'A decade of innovating in the digital landscape.',
      icon: 'ri-calendar-check-line',
      color: 'from-orange-500 to-red-500'
    },
    {
      number: 15000,
      suffix: '+',
      label: 'Orders Fulfilled',
      description: 'Efficiency and reliability across every project.',
      icon: 'ri-check-double-line',
      color: 'from-red-500 to-pink-500'
    },
    {
      number: 110,
      suffix: '+',
      label: 'Brands Transformed',
      description: 'Helping businesses reach new heights.',
      icon: 'ri-building-4-line',
      color: 'from-pink-500 to-purple-500'
    },
    {
      number: 3000,
      suffix: '+',
      label: 'Logos Created',
      description: 'Crafting iconic identities that stand the test of time.',
      icon: 'ri-pencil-ruler-2-line',
      color: 'from-purple-500 to-orange-500'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const duration = 2000;
      const steps = 60;
      const stepDuration = duration / steps;

      milestones.forEach((milestone, index) => {
        let currentStep = 0;
        const increment = milestone.number / steps;
        
        const timer = setInterval(() => {
          currentStep++;
          const currentValue = Math.min(Math.floor(increment * currentStep), milestone.number);
          
          setCounters(prev => ({
            ...prev,
            [index === 0 ? 'years' : index === 1 ? 'orders' : index === 2 ? 'brands' : 'logos']: currentValue
          }));

          if (currentStep >= steps) {
            clearInterval(timer);
          }
        }, stepDuration);
      });
    }
  }, [isVisible]);

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-20 bg-black relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Milestones & Impact:
            </span>
            <br />
            <span className="text-white">Our Journey in Numbers</span>
          </h2>

          <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Over the past decade, we've partnered with diverse brands across India and beyond. 
            Our journey is a testament to the trust our clients place in us.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {milestones.map((milestone, index) => {
            const currentValue = index === 0 ? counters.years : 
                               index === 1 ? counters.orders : 
                               index === 2 ? counters.brands : 
                               counters.logos;

            return (
              <div
                key={index}
                className={`group relative p-6 md:p-8 rounded-2xl bg-gray-900 border border-gray-800 hover:border-orange-500/50 transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <div className={`w-14 h-14 rounded-full bg-gradient-to-r ${milestone.color} flex items-center justify-center mb-6`}>
                  <i className={`${milestone.icon} text-white text-2xl`}></i>
                </div>

                <div className="mb-2">
                  <span className="text-3xl md:text-4xl font-bold text-white">
                    {currentValue.toLocaleString()}
                  </span>
                  <span className="text-2xl font-bold text-orange-400">{milestone.suffix}</span>
                </div>

                <h3 className="text-lg font-bold text-gray-200 mb-2">
                  {milestone.label}
                </h3>

                <p className="text-gray-400 text-sm leading-relaxed">
                  {milestone.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className={`text-center mt-12 md:mt-16 transition-all duration-700 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Be Part of Our Success Story?
            </h3>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Join the growing list of brands that have transformed their digital presence with Hash Adz.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="group bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105">
                Start Your Journey
              </Link>
              
              <Link to="/contact" className="group border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}